import { BehaviorSubject, Observable, take } from 'rxjs';

/**
 * @module SchedulerService
 */
function createSchedulerService(apiService, logService, util, _window) {
    const _schedulerService = {};
    const schedulerDeviceSubjects = {};
    const schedulerDeviceSubscriptions = {};
    const schedulerSubjects = {};

    const functionEnum = Object.freeze({
        camMotion: 'cam/motion',
        feedMotion: 'feed/motion',
        feedNotification: 'feed/notification',
        lightProgram: 'light/program'
    });

    const typeEnum = Object.freeze({
        action: 'action',
        filter: 'filter'
    });

    function log(params) {
        params.module = 'schedulerService';
        logService.log(params);
        return params;
    }

    function schedulerSort(a, b) {
        if (a.function < b.function) {
            return -1;
        }
        if (a.function > b.function) {
            return 1;
        }
        if (a.type < b.type) {
            return -1;
        }
        if (a.type > b.type) {
            return 1;
        }
        if (a.lastModTime.getTime() < b.lastModTime.getTime()) {
            return 1;
        }
        if (a.lastModTime.getTime() > b.lastModTime.getTime()) {
            return -1;
        }
        return 0;
    }

    function filterFeedNotificationsSort(a, b) {
        if (a.lastModTime.getTime() === b.lastModTime.getTime()) {
            return 0;
        }
        if (a.lastModTime.getTime() < b.lastModTime.getTime()) {
            return 1;
        }
        return -1;
    }

    function processResults(schedulerArray, params) {
        schedulerArray.forEach(scheduler => {
            // convert date strings to Date objects
            if (typeof scheduler.lastModTime === 'string') {
                scheduler.lastModTime = new _window.Date(scheduler.lastModTime);
            }
            if (typeof scheduler.nextFire === 'string') {
                scheduler.nextFire = new _window.Date(scheduler.nextFire);
            }

            // check for unknown property values
            if (scheduler.function && Object.values(functionEnum).indexOf(scheduler.function) === -1) {
                log({
                    code: 'processImportedData',
                    descr: 'unknownFunction',
                    object: scheduler,
                    traceId: params?.traceId
                });
            }
            if (scheduler.type && Object.values(typeEnum).indexOf(scheduler.type) === -1) {
                log({
                    code: 'processImportedData',
                    descr: 'unknownType',
                    object: scheduler,
                    traceId: params?.traceId
                });
            }
        });

        // for filterFeedNotifications, only keep the most recently modified one
        const filterFeedNotifications = schedulerArray.filter(
            s => s.type === 'filter' && s.function === 'feed/notification'
        ).sort(filterFeedNotificationsSort);
        if (filterFeedNotifications.length > 1) {
            remove(filterFeedNotifications[1], params).subscribe();
            schedulerArray.splice(schedulerArray.indexOf(filterFeedNotifications[1]), 1);
        }
    }

    function idFilter(filter, scheduler) {
        if (!filter?.id) {
            return true;
        }
        return filter.id === scheduler.id;
    }

    function deviceIdFilter(filter, scheduler) {
        if (!filter?.deviceId) {
            return true;
        }
        return filter.deviceId === scheduler.deviceId;
    }

    function functionFilter(filter, scheduler) {
        if (!filter?.functionType) {
            return true;
        }
        return filter.functionType === scheduler.function;
    }

    function typeFilter(filter, scheduler) {
        if (!filter || !filter.schedulerType) {
            return true;
        }
        return filter.schedulerType === scheduler.type;
    }

    /**
     * @description Get and retain scheduler data for one device
     * @param {String} params.deviceId The device for get scheduler data for
     * @param {Boolean} params.refresh truthy to refresh data
     * @param {String} [params.traceId]
     * @returns {Observable<Array<Scheduler>>} emits an array of schedulers for a device
     */
    function getSchedulerDeviceObservable(params) {
        let isNew;
        if (!schedulerDeviceSubjects[params.deviceId]) {
            schedulerDeviceSubjects[params.deviceId] = new BehaviorSubject();
            isNew = true;
        }
        if (isNew || params.refresh) {
            apiService.schedulers.getForDevice(params).then(
                result => {
                    processResults(result.data.list, params);
                    if (schedulerDeviceSubjects[params.deviceId]) {
                        schedulerDeviceSubjects[params.deviceId].next(result.data.list);
                    }
                },
                () => {
                    if (schedulerDeviceSubjects[params.deviceId]) {
                        schedulerDeviceSubjects[params.deviceId].next();
                    }
                }
            );
        }
        return schedulerDeviceSubjects[params.deviceId].asObservable();
    }

    /**
     * @description Removes a Scheduler
     * @param {Object} scheduler The Scheduler to remove. Required.
     * @param {String} scheduler.id Required.
     * @param {String} scheduler.deviceId Required.
     * @param {Object} [params]
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the scheduler has been removed
     */
    function remove(scheduler, params) {
        if (!scheduler?.id || ! scheduler?.deviceId) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'removeBadParams',
                    object: scheduler,
                    traceId: params?.traceId
                })
            ));
        }

        return new Observable(subscriber => {
            apiService.schedulers.remove({
                schedulerId: scheduler.id,
                traceId: params?.traceId
            }).then(
                () => {
                    _schedulerService.get({
                        deviceId: scheduler.deviceId,
                        refresh: true,
                        traceId: params?.traceId
                    }).pipe(take(2)).subscribe(() => {
                        subscriber.next();
                        subscriber.complete();
                    });
                },
                e => subscriber.error(
                    log({
                        code: 'removeFailed',
                        object: scheduler,
                        error: e,
                        traceId: params?.traceId
                    })
                )
            );
        });
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @property functionEnum
     * @name functionEnum
     */
    _schedulerService.functionEnum = functionEnum;

    /**
     * @property typeEnum
     * @name typeEnum
     */
    _schedulerService.typeEnum = typeEnum;

    /**
     * @description Gets a filtered array of Schedlers
     * @param {Object} params Required.
     * @param {String} params.deviceId The device to get schedulers for. Required.
     * @param {String} params.functionType "cam/motion", "feed/motion", "feed/notification" or "light/program"
     * @param {String} params.schedulerType "action" or "filter"
     * @param {String} params.id The schedulerId - this param probably not needed
     * @param {Boolean} params.refresh truthy to refresh data from the server
     * @param {String} [params.traceId]
     * @returns {Observable<Array<Schdeulers>>}
     * @name get
     * @method
     */
    _schedulerService.get = function(params) {
        if (!params?.deviceId) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'getBadParams',
                    object: params,
                    traceId: params?.traceId
                })
            ));
        }

        const key = JSON.stringify({
            deviceId: params.deviceId,
            functionType: params.functionType,
            schedulerType: params.schedulerType,
            id: params.id
        });

        schedulerSubjects[key] ??= new BehaviorSubject();

        if (schedulerDeviceSubscriptions[key] && params.refresh) {
            schedulerDeviceSubscriptions[key].unsubscribe();
            schedulerDeviceSubscriptions[key] = undefined;
        }

        if (!schedulerDeviceSubscriptions[key]) {
            const internalParams = util.withTraceId(params);
            schedulerDeviceSubscriptions[key] = getSchedulerDeviceObservable(internalParams).subscribe({
                next: list => {
                    if (list) {
                        schedulerSubjects[key].next(
                            list.filter(scheduler => {
                                return deviceIdFilter(internalParams, scheduler)
                                        && functionFilter(internalParams, scheduler)
                                        && typeFilter(internalParams, scheduler)
                                        && idFilter(internalParams, scheduler);
                            }).sort(schedulerSort)
                        );
                    } else {
                        schedulerSubjects[key].next();
                    }
                },
                error: util.noop
            });
        }

        return schedulerSubjects[key].asObservable();
    };

    /**
     * @description Creates or updates a scheduler
     * @param {Object} params
     * @param {Scheduler} params.scheduler The scheduler to create or update
     * @param {String} params.scheduler.id Must be provided for existing schedulers, or undefined for new schedulers
     * @param {String} params.scheduler.deviceId Must be provided
     * @param {String} [params.traceId]
     * @returns {Observable<Scheduler>} - emits a saved scheduler
     * @name save
     * @method
     */
    _schedulerService.save = function(params) {
        return new Observable(subscriber => {
            if (!params?.scheduler?.deviceId) {
                subscriber.error(
                    log({
                        code: 'saveBadParams',
                        object: params?.scheduler,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            const method = (internalParams.scheduler.id) ? 'put' : 'post';
            apiService.schedulers[method](internalParams).then(
                postResult => {
                    _schedulerService.get({
                        deviceId: internalParams.scheduler?.deviceId,
                        refresh: true,
                        traceId: internalParams.traceId
                    }).pipe(take(2)).subscribe(() => {
                        subscriber.next(postResult.data);
                        subscriber.complete();
                    });
                },
                e => subscriber.error(
                    log({
                        code: 'saveFailed',
                        object: internalParams.scheduler,
                        deviceId: internalParams.scheduler?.deviceId,
                        error: e
                    })
                )
            );
        });
    };

    return Object.freeze(_schedulerService);
}

export default createSchedulerService;
